import React, { useState, useEffect } from "react";
import moment from "moment";

const Dashboard = ({
  config,
  upcoming,
  past,
  onAddReservation,
  onReschedule,
  onCancel,
  onViewMenu,
  onRate,
}) => {
  const [activeTab, setActiveTab] = useState("upcoming");
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  useEffect(() => {
    const loadCSS = (url, id) => {
      if (!document.getElementById(id)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = url;
        link.id = id;
        document.head.appendChild(link);
      }
    };
    loadCSS(
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css",
      "bootstrap-icons"
    );
  }, []);

  const restaurantName = config.company_name || "Barbeque Nation";

  const renderReservationCard = (res, isUpcoming) => (
    <div
      className={`pos-res-card status-${res.status.toLowerCase()}`}
      key={res.id}
    >
      <div className="pos-res-header">
        <div className="pos-res-date">
          <i className="bi bi-calendar me-2"></i>
          <strong>{moment(res.start).format("MMM D, YYYY h:mm A")}</strong>
        </div>
        <span
          className={`pos-badge ${
            res.status === "Confirmed"
              ? "pos-badge-success"
              : res.status === "Pending"
              ? "pos-badge-warning"
              : res.status === "Cancelled"
              ? "pos-badge-danger"
              : "pos-badge-secondary"
          }`}
        >
          {res.status}
        </span>
      </div>
      <div className="pos-res-body">
        <div className="pos-res-item">
          <i className="bi bi-people me-1 text-primary"></i>
          {res.partySize} Guests
        </div>
        {res.notes && (
          <div className="pos-res-item muted">
            <i className="bi bi-chat-left-text me-1"></i>
            {res.notes}
          </div>
        )}
      </div>
      {isUpcoming && res.status !== "Cancelled" && (
        <div className="pos-res-footer">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => onReschedule(res)}
          >
            <i className="bi bi-pencil me-1"></i> Reschedule
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => {
              setCancelTarget(res);
              setShowCancelPrompt(true);
            }}
          >
            <i className="bi bi-trash me-1"></i> Cancel
          </button>
        </div>
      )}
      {!isUpcoming && res.status === "Confirmed" && (
        <div className="pos-res-footer">
          <button
            className="btn btn-sm btn-success"
            onClick={() => onRate(res)}
          >
            <i className="bi bi-emoji-smile me-1"></i> Rate Your Dine
          </button>
        </div>
      )}
    </div>
  );

  const renderEmptyState = (isUpcoming) => (
    <div className="pos-res-empty text-center py-5 text-muted">
      <p>
        No {isUpcoming ? "upcoming" : "past"} reservations yet. <br />
        {isUpcoming
          ? `Ready to book your next experience at ${restaurantName}?`
          : "Your booking history is empty."}
      </p>
      {isUpcoming && (
        <button className="btn btn-primary mt-3" onClick={onAddReservation}>
          Make a Reservation
        </button>
      )}
    </div>
  );

  return (
    <div className="pos-dashboard">
      {/* Heading and Add Reservation Button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h2 style={{ color: config.colors.primary }}>RESERVATIONS</h2>
        <button
          className="btn pos-add-res-btn"
          style={{ background: config.colors.primary, color: "#fff", border: "none", padding: "10px 24px", borderRadius: "8px", fontWeight: 600, cursor: "pointer", transition: "background 0.2s ease" }}
          onClick={onAddReservation}
        >
          <i className="bi bi-plus me-1"></i> Add Reservation
        </button>
      </div>
      {/* Tabs */}
      <ul className="pos-nav-tabs">
        <li
          className={activeTab === "upcoming" ? "active" : ""}
          onClick={() => setActiveTab("upcoming")}
        >
          Upcoming Reservations
        </li>
        <li
          className={activeTab === "history" ? "active" : ""}
          onClick={() => setActiveTab("history")}
        >
          Reservation History
        </li>
      </ul>
      {/* Reservations */}
      <div className="pos-res-grid">
        {activeTab === "upcoming"
          ? upcoming.length === 0
            ? renderEmptyState(true)
            : upcoming.map((res) => renderReservationCard(res, true))
          : past.length === 0
          ? renderEmptyState(false)
          : past.map((res) => renderReservationCard(res, false))}
      </div>
      {/* Cancel Confirmation Modal */}
      {showCancelPrompt && (
        <div className="cancel-modal">
          <div className="cancel-modal-content">
            <h5>Cancel Reservation?</h5>
            <p>
              Are you sure you want to cancel your reservation on{" "}
              <strong>{moment(cancelTarget.start).format("MMM D, YYYY h:mm A")}</strong>?
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  onCancel(cancelTarget.id);
                  setShowCancelPrompt(false);
                  setCancelTarget(null);
                }}
              >
                Yes, Cancel
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setShowCancelPrompt(false);
                  setCancelTarget(null);
                }}
              >
                No, Keep It
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Scoped Styles */}
      <style>{`
        .pos-dashboard {
          font-family: "Arial, sans-serif";
          padding: 24px;
          background: #f9fafb;
          max-width: 1200px;
          margin: 0 auto;
        }
        h2 {
          font-size: 24px;
          font-weight: 700;
          margin: 0;
        }
        .pos-nav-tabs {
          display: flex;
          list-style: none;
          padding: 0;
          margin: 0 0 24px 0;
          border-bottom: 2px solid #e5e7eb;
        }
        .pos-nav-tabs li {
          margin-right: 30px;
          padding: 10px 0;
          cursor: pointer;
          font-weight: 600;
          color: #4b5563;
          position: relative;
          font-size: 16px;
        }
        .pos-nav-tabs li.active {
          color: #1d4ed8;
        }
        .pos-nav-tabs li.active::after {
          content: "";
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 100%;
          height: 3px;
          background: #1d4ed8;
        }
        .pos-res-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
        }
        .pos-res-card {
          background: #ffffff;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border-left: 6px solid transparent;
        }
        .pos-res-card.status-confirmed {
          border-left-color: #10b981;
        }
        .pos-res-card.status-pending {
          border-left-color: #f59e0b;
        }
        .pos-res-card.status-cancelled {
          border-left-color: #ef4444;
        }
        .pos-res-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.1);
        }
        .pos-res-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
        }
        .pos-res-date {
          display: flex;
          align-items: center;
          font-size: 15px;
          font-weight: 600;
          color: #374151;
        }
        .pos-badge {
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
        }
        .pos-badge-success { background: #10b981; color: #fff; }
        .pos-badge-warning { background: #f59e0b; color: #fff; }
        .pos-badge-danger { background: #ef4444; color: #fff; }
        .pos-badge-secondary { background: #6b7280; color: #fff; }
        .pos-res-body {
          font-size: 14px;
          margin-bottom: 16px;
          color: #4b5563;
        }
        .pos-res-item {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
        }
        .pos-res-item.muted {
          color: #6b7280;
          font-style: italic;
        }
        .pos-res-footer {
          display: flex;
          gap: 12px;
        }
        .pos-res-empty {
          grid-column: 1 / -1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #6b7280;
        }
        .pos-add-res-btn:hover {
          background: ${config.colors.primary && config.colors.primary.replace('0.2s ease', '0.2s ease, opacity 0.2s ease') || '#1e40af'};
          opacity: 0.9;
        }
        .cancel-modal {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }
        .cancel-modal-content {
          background: #fff;
          border-radius: 12px;
          padding: 24px;
          max-width: 450px;
          text-align: center;
          box-shadow: 0 8px 24px rgba(0,0,0,0.2);
        }
        .cancel-modal h5 {
          margin-bottom: 16px;
          color: #ef4444;
          font-size: 18px;
        }
        .cancel-modal p {
          margin-bottom: 24px;
          color: #374151;
          font-size: 14px;
        }
        .modal-actions {
          display: flex;
          justify-content: center;
          gap: 16px;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;